import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, CloudIcon } from "@heroicons/react/24/outline";
import { mapSevenDayForecastRows } from "../services/forecastService";
import TempRangeBar from "./TempRangeBar.jsx";
import sunrise2 from "../assets/dashboard-ui/sunrise2.png";
import sunset2 from "../assets/dashboard-ui/sunset2.png";
import { glass } from "../styles/glass";

const HEADER_GRID =
  "flex justify-between grid w-full items-center gap-3 px-4 py-3.5 sm:gap-4 sm:px-5 [grid-template-columns:minmax(2.75rem,3rem)_2rem_minmax(3.25rem,4.5rem)_3.25rem_2.75rem_5rem_2.75rem_1.25rem]";

const CELL_TEXT = "text-lg font-medium leading-none";

function ChevronToggle({ expanded }) {
  return (
    <motion.span
      className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-400"
      animate={{ rotate: expanded ? 180 : 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      aria-hidden
    >
      <ChevronDownIcon className="h-5 w-5" />
    </motion.span>
  );
}

function DayWeatherIcon({ iconSrc, className = "" }) {
  if (iconSrc) {
    return (
      <img
        src={iconSrc}
        alt=""
        className={`h-10 w-10 shrink-0 object-contain ${className}`}
      />
    );
  }
  return <span className={`h-10 w-10 shrink-0 ${className}`} />;
}

function DayRowSummary({
  day,
  weekMinF,
  weekMaxF,
  expanded,
  onToggle,
  getWeatherIconUrl,
}) {
  const iconSrc = getWeatherIconUrl
    ? getWeatherIconUrl(day.iconKey)
    : null;

  const precipLabel =
    day.pop != null ? `${String(day.pop).padStart(2, "0")}%` : "—";

  const lowLabel =
    day.minF != null ? `L:${Math.round(day.minF)}°` : "L:—";
  const highLabel =
    day.maxF != null ? `H:${Math.round(day.maxF)}°` : "H:—";

  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full text-left transition-colors hover:bg-white/5"
    >
      {/* Mobile */}
      <div className="flex flex-col gap-2 p-4 md:hidden">
        <div className="flex items-center justify-between gap-2">
          <span className={`${CELL_TEXT} text-white`}>{day.dayLabel}</span>
          <div className="flex items-center gap-2">
            <DayWeatherIcon iconSrc={iconSrc} />
            <ChevronToggle expanded={expanded} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-white">
            {day.condition}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-white">
            <CloudIcon className="h-5 w-5 shrink-0 text-white" aria-hidden />
            <span>{precipLabel}</span>
          </span>
        </div>
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
          <span className={`${CELL_TEXT} text-gray-300`}>{lowLabel}</span>
          <TempRangeBar
            minF={day.minF}
            maxF={day.maxF}
            weekMinF={weekMinF}
            weekMaxF={weekMaxF}
          />
          <span className={`${CELL_TEXT} text-gray-300`}>{highLabel}</span>
        </div>
      </div>

      {/* Desktop */}
      <div
        className={`${HEADER_GRID} hidden w-full md:grid`}
      >
        <span className={`${CELL_TEXT} text-white`}>{day.dayLabel}</span>

        <DayWeatherIcon iconSrc={iconSrc} className="justify-self-center" />

        <span className={`${CELL_TEXT} truncate text-white`}>
          {day.condition}
        </span>

        <span
          className={`flex items-center justify-start gap-1 ${CELL_TEXT} text-white`}
        >
          <CloudIcon className="h-10 w-10 shrink-0 text-white" aria-hidden />
          <span>{precipLabel}</span>
        </span>

        <span className={`${CELL_TEXT} text-gray-300`}>{lowLabel}</span>

        <TempRangeBar
          minF={day.minF}
          maxF={day.maxF}
          weekMinF={weekMinF}
          weekMaxF={weekMaxF}
        />

        <span className={`${CELL_TEXT} text-gray-300`}>{highLabel}</span>

        <span className="justify-self-end">
          <ChevronToggle expanded={expanded} />
        </span>
      </div>
    </button>
  );
}

function DayRowDetails({ day }) {
  const metrics = [
    { label: "Humidity", value: day.humidity != null ? `${day.humidity}%` : "—" },
    {
      label: "Wind",
      value: day.windMph != null ? `${day.windMph} mph` : "—",
    },
    { label: "UV Index", value: day.uvi != null ? String(day.uvi) : "—" },
    { label: "Date", value: day.dateLabel },
  ];

  return (
    <div className="border-t border-borderGray px-4 pb-5 pt-3 sm:px-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {metrics.map(({ label, value }) => (
          <div
            key={label}
            className={`${glass} px-4 py-4 sm:py-5`}
          >
            <p className="text-sm text-gray-400">{label}</p>
            <p className="mt-2 text-lg font-semibold text-white sm:text-xl">
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className={`mt-4 flex items-center justify-between gap-6 px-5 py-5 sm:px-6 sm:py-6 ${glass}`}>
        <div className="flex items-center gap-3 sm:gap-4">
          <img
            src={sunrise2}
            alt=""
            className="h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14"
          />
          <div>
            <p className="text-sm text-gray-400">Sunrise</p>
            <p className="mt-1 text-base font-semibold text-white sm:text-lg">
              {day.sunriseLabel}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-400">Sunset</p>
            <p className="mt-1 text-base font-semibold text-white sm:text-lg">
              {day.sunsetLabel}
            </p>
          </div>
          <img
            src={sunset2}
            alt=""
            className="h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14"
          />
        </div>
      </div>
    </div>
  );
}

function AccordionPanel({ day }) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="overflow-hidden"
    >
      <DayRowDetails day={day} />
    </motion.div>
  );
}

export default function SevenDayForecastAccordion({
  dailyForecastRaw,
  loading,
  getWeatherIconUrl,
}) {
  const { days, weekMinF, weekMaxF } = useMemo(
    () => mapSevenDayForecastRows(dailyForecastRaw),
    [dailyForecastRaw]
  );

  const daysKey = days.map((d) => d.id).join("|");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const defaultId =
      days.find((d) => d.isToday)?.id ?? days[0]?.id ?? null;
    setExpandedId(defaultId);
  }, [daysKey]);

  if (loading && !days.length) {
    return (
      <div className={`${glass} p-5`}>
        <h2 className="text-lg font-semibold text-white">7-Day Forecast</h2>
        <div className="mt-4 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-white/5"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!days.length) return null;

  return (
    <div className={`${glass} p-4 sm:p-5`}>
      <h2 className="px-1 text-lg font-semibold text-white">7-Day Forecast</h2>

      <div className="mt-3 space-y-2.5">
        {days.map((day) => {
          const expanded = expandedId === day.id;
          return (
            <div
              key={day.id}
              className={`overflow-hidden shadow-sm ${glass}`}
            >
              <DayRowSummary
                day={day}
                weekMinF={weekMinF}
                weekMaxF={weekMaxF}
                expanded={expanded}
                onToggle={() => setExpandedId(expanded ? null : day.id)}
                getWeatherIconUrl={getWeatherIconUrl}
              />
              <AnimatePresence initial={false}>
                {expanded && <AccordionPanel day={day} />}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
